import React, { useState, useEffect, useRef } from "react";
import { siteTime, siteDateTime } from "./siteTime.js";
import { leadInMs, replayClockValue, remainingWait, tailMs } from "./replayClock.js";

// Everything the Demo Comps replay page and the admin Competition
// History modal both need, extracted here so there is exactly ONE
// implementation of the playback loop rather than two that drift.
//
// This file was created by moving code verbatim out of
// DemoCompsReplayPage.jsx, not by rewriting it. That matters: the
// playback loop below carries two real, hard-won bug fixes (see its
// own comments about stale sibling-effect state), and a second,
// independently-written copy for the admin modal would have been an
// invitation to reintroduce exactly those. The page's own
// between-competition gap screen deliberately stayed behind in that
// file — it only makes sense for a 5-competition demo round, and the
// admin modal plays a single competition and stops.

// Mirrors formatLongCountdown from App.jsx — this can be days away in a
// real competition, though for a demo round it's always short (~1
// minute), since mock competitions are built that way.
export function formatLongCountdown(ms) {
  if (ms <= 0) return "0:00";
  const totalSeconds = Math.ceil(ms / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

// A visual-only stand-in for App.jsx's own GuessForm — same layout and
// copy (including the exact lottery-mode replacement text), but no
// real backend submission, since this is a replay, not a real site
// visit. isLottery is computed by the caller from the competition's own
// recorded lotteryConvertAt against the currently playing event's own
// timestamp, mirroring how the real homepage computes its own isLottery
// flag from config.lotteryConvertAt.
export function ReplayGuessForm({ answerMode, creditsPerEntry, creditsPerMajorUnitOnPixels, currencySymbol, isLottery }) {
  const [guess, setGuess] = useState("");
  const [submitted, setSubmitted] = useState(false);

  if (isLottery) {
    return (
      <div style={{ marginTop: 20, padding: 14, border: "1px solid var(--accent-yellow)", borderRadius: 8, color: "var(--accent-yellow)" }}>
        Entries are frozen — this competition has moved to a lottery draw. A winner will be picked randomly from
        purchased pixels at the finish time. You can still buy more of the image for better odds.
      </div>
    );
  }

  return (
    <div style={{ marginTop: 20, padding: 14, border: "1px solid var(--border-color)", borderRadius: 8 }}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setSubmitted(true);
          setGuess("");
          setTimeout(() => setSubmitted(false), 2000);
        }}
        style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}
      >
        <input
          value={guess}
          onChange={(e) => setGuess(e.target.value)}
          placeholder="Guess what the hidden image is…"
          style={{ flex: 1, minWidth: 200, padding: 8, background: "var(--bg-input)", border: "1px solid var(--border-color-light)", borderRadius: 6, color: "var(--text-primary)" }}
        />
        <button type="submit" disabled={!guess.trim()}>
          {submitted ? "✓ Submitted" : "Submit guess"}
        </button>
      </form>
      <div style={{ fontSize: 12, opacity: 0.6, marginTop: 6 }}>
        {answerMode === "exact" ? "Exact match required" : "Fuzzy match accepted"}
        {creditsPerEntry > 0 && ` · ${creditsPerEntry} credit${creditsPerEntry === 1 ? "" : "s"} per entry`}
        {creditsPerMajorUnitOnPixels > 0 &&
          ` · earn ${creditsPerMajorUnitOnPixels} credits for every ${currencySymbol}1 you spend uncovering the image`}
      </div>
    </div>
  );
}

// Mirrors the public half of App.jsx's own StatsPanel exactly (copy and
// layout) — the per-user "My stats" box that panel also shows doesn't
// apply here at all, since a replay viewer is never logged in.
// imagesRevealed is a count of revealed regions, not a percentage (see
// the backend's own imagesRevealed field) — matched here directly via
// purchaseCount, the same count already driving the image itself.
export function ReplayStatsPanel({ imagesRevealed, usersEntered, entriesSubmitted }) {
  return (
    <div style={{ width: "100%", border: "1px solid var(--border-color)", borderRadius: 8, padding: 12, boxSizing: "border-box" }}>
      <div style={{ fontSize: 13, opacity: 0.6, marginBottom: 4 }}>This competition</div>
      <div style={{ fontSize: 14, marginBottom: 2 }}>Images revealed: {imagesRevealed}</div>
      <div style={{ fontSize: 14, marginBottom: 2 }}>Users on this competition: {usersEntered}</div>
      <div style={{ fontSize: 14 }}>Entries submitted: {entriesSubmitted}</div>
    </div>
  );
}

// Mirrors App.jsx's own LastEntriesPanel exactly (copy, layout, the
// six-entry cap shown most-recent-first, and the same correct-guess
// highlighting) — driven by the replay's own played-so-far guesses
// rather than a live poll.
export function ReplayLastEntriesPanel({ guesses }) {
  const recent = guesses.slice(-6).reverse();
  return (
    <div style={{ width: "100%", border: "1px solid var(--border-color)", borderRadius: 8, padding: 12, boxSizing: "border-box" }}>
      <div style={{ fontSize: 13, opacity: 0.6, marginBottom: 8 }}>Last entries</div>
      {recent.length === 0 ? (
        <p style={{ fontSize: 13, opacity: 0.7 }}>No entries yet — be the first to guess!</p>
      ) : (
        recent.map((g, i) => (
          <div key={i} style={{ fontSize: 12, marginBottom: 8, paddingBottom: 8, borderBottom: "1px solid var(--border-color-subtle)" }}>
            <div>
              <strong>{g.buyerUsername || "A player"}</strong>{" "}
              <span style={{ color: g.correct ? "var(--accent-green)" : "var(--text-secondary)" }}>
                {g.correct ? `guessed "${g.guess}" — correct!` : `guessed "${g.guess}"`}
              </span>
            </div>
            <div style={{ opacity: 0.5 }}>{siteTime(g.createdAt)}</div>
          </div>
        ))
      )}
    </div>
  );
}

// Merges a competition's own regions and guesses into one,
// chronologically-sorted list of playback events — the actual,
// recorded order everything happened in, which is the entire point of
// this feature: nothing here is newly recorded, just read back out in
// order (see the backend's own buildCompetitionReplayPayload comment).
export function buildEventTimeline(comp) {
  const events = [];
  comp.regions.forEach((r) => events.push({ type: "purchase", at: r.purchasedAt, region: r }));
  comp.guesses.forEach((g) => events.push({ type: "guess", at: g.createdAt, guess: g }));
  events.sort((a, b) => a.at - b.at);
  return events;
}

// 1 IS REAL TIME, and it means it — dead air included.
//
// The gaps used to be clamped to at most 2.5 seconds, so a competition
// where nothing happened for ten minutes replayed that stretch in a
// couple of seconds. Convenient, and a lie: a replay that quietly
// compresses time is not a recording of what happened, and a viewer who
// watched the original would know it.
//
// Decided 14 Sep 2026: show what happened, and let the speed controls
// deal with the quiet stretches.
//
// WHICH IS WHY 60 AND 600 ARE HERE. A competition that ran for two days
// takes two days at 1x, and even 10x leaves five hours. The fast options
// are how somebody gets past an empty night to the part they care about
// — an honest recording needs a way to skim, or nobody watches it.
export const SPEEDS = [1, 2, 5, 10, 60, 600];
// A real gap between two recorded events can be many seconds (the
// simulation ticks every few seconds) — capped here so playback never
// stalls on an awkwardly long pause, regardless of how sparse a
// particular stretch of the real history was.
// THE STEP CEILING IS GONE. It clamped every gap to 2.5 seconds, which
// made a quiet competition watchable and made the replay a lie. A test
// asserts it stays gone — the temptation to reinstate it will come back
// the first time somebody watches an empty night. See SPEEDS.
//
// The pause after the last event before the picture is revealed. Not
// part of the recording — a moment to look at the finished grid — so it
// scales with speed like everything else.
export const REVEALED_HOLD_MS = 3500;
// What to wait after the final event, having no next one to measure to.
export const TAIL_GAP_MS = 800;

/*
 * WHERE IN THE COMPETITION THIS IS (1.515.0).
 *
 * Asked for on 27 Sep 2026: "a timer which spans the length of the
 * competition and speeds up depending on what speed control is clicked".
 *
 * BESIDE THE COUNT BAR, NOT INSTEAD OF IT, which was the choice put and
 * taken. The bar above answers "how much of this have I seen"; a
 * competition's own clock answers "when did this happen", and a replay of
 * a quiet night looks identical to a busy one without it. What was
 * rejected was a scrubber spanning the whole duration: a week does not
 * fit on a bar, and at 1x the handle would not visibly move.
 *
 * A VIRTUAL CLOCK, not a count of real milliseconds. It advances at the
 * chosen speed because that is what the playback does, it freezes when
 * paused because the wait in flight freezes, and it is clamped to the
 * competition's own end so a late timer cannot run it past the finish.
 *
 * Pure, so the widget's own copy can be held to the same answers by the
 * same test - the duplication that let the replay widget keep a broken
 * copy of the frame logic is recorded further down this file.
 */
export function virtualElapsed({ anchorAt, since, speed, startedAt, endedAt, now }) {
  if (!Number.isFinite(startedAt) || !Number.isFinite(endedAt) || endedAt <= startedAt) return null;
  // since == null means paused: the clock is already frozen at anchorAt.
  const at = since == null ? anchorAt : anchorAt + (now - since) * speed;
  const clamped = Math.min(Math.max(at, startedAt), endedAt);
  return { elapsed: clamped - startedAt, total: endedAt - startedAt };
}

// WHERE THE CLOCK HAS REACHED, in the competition's own milliseconds.
//
// The one piece of arithmetic behind both things that interrupt a
// running clock: a pause freezes it here, and a SPEED CHANGE re-anchors
// it here. Without the re-anchor, virtualElapsed reprices every real
// millisecond already watched at the new speed: ten seconds at 1x
// followed by pressing 600x jumped the clock from 10s to 6000s
// (reproduced before it was fixed, and pinned by a test below).
//
// Pure and shared for the same reason virtualElapsed is - the widget's
// ES5 copy is held to these answers by the same test.
export function advanceClock({ anchorAt, since, speed, now }) {
  if (since == null || anchorAt == null) return anchorAt;
  return anchorAt + (now - since) * speed;
}

// "2d 4h", "3h 12m", "6m 20s", "45s". TWO UNITS AT MOST: this sits in a
// line of small text beside the counts, and "2d 4h 12m 9s" is a number
// nobody reads. The larger unit is the one that carries the meaning.
export function formatSpan(ms) {
  const t = Math.max(0, Math.round(Number(ms) || 0) / 1000);
  const d = Math.floor(t / 86400);
  const h = Math.floor((t % 86400) / 3600);
  const m = Math.floor((t % 3600) / 60);
  const sec = Math.floor(t % 60);
  if (d > 0) return h > 0 ? `${d}d ${h}h` : `${d}d`;
  if (h > 0) return m > 0 ? `${h}h ${m}m` : `${h}h`;
  if (m > 0) return sec > 0 ? `${m}m ${sec}s` : `${m}m`;
  return `${sec}s`;
}

// Mirrors the real site's own usersEnteredCompetition calculation (a
// union of buyer and entrant user ids) — matched here via username,
// the closest equivalent available in the replay's own data shape.
export function countUsersEntered(playedRegions, playedGuesses) {
  return new Set([...playedRegions.map((r) => r.buyerUsername), ...playedGuesses.map((g) => g.buyerUsername)].filter(Boolean)).size;
}

// The step-by-step playback itself — advances one event at a time,
// paced by the real recorded gap between events (scaled down by the
// chosen speed, capped so a sparse stretch never stalls playback).
// A purchase event just bumps purchaseCount, which the caller builds
// the displayed image's own URL from — the browser then re-fetches a
// fresh, server-composited image on each change, the exact same
// pattern the real site's own frontend already uses for its live
// composite (see buildCompetitionImageBuffer's own callers), rather
// than any client-side compositing.
//
// Returns speedRef as well as the state, so a caller with its own
// timers (the demo page's between-competition gap screen) can pace
// them by the same live speed value without re-deriving it.
// `asItHappened` (1.537.0) restores the two stretches this player has
// always cut: see LEAD_IN and the tail gap below.
export function useReplayPlayback(comp, events, speed, asItHappened = false) {
  const [phase, setPhase] = useState("playing"); // "playing" | "revealed"
  // PAUSE, RESUME AND RESTART (1.400.0, 20 Sep 2026).
  //
  // The partner's own page has had Play/Pause and Restart since it was
  // written; this site's player had speed buttons and nothing else, so a
  // viewer could not stop on the moment they wanted to look at. Reported
  // as: "the 589comps.com has better controls".
  //
  // Built on the SAME pending-wait machinery the speed buttons use: what
  // is stored is the remaining VIRTUAL time of the wait in flight, so
  // pausing at 600x and resuming at 1x resumes the wait that is actually
  // left rather than restarting it.
  const [playing, setPlaying] = useState(true);
  const playingRef = useRef(true);
  // Bumped by restart(), which the playback effect depends on — the way
  // to re-run a recording from the top without remounting the player.
  const [runId, setRunId] = useState(0);
  const [purchaseCount, setPurchaseCount] = useState(0); // how many purchase events have played so far, for the image URL's own regionCount
  const [guessCount, setGuessCount] = useState(0); // how many guess events have played so far — comp.guesses is already sorted chronologically, so slicing it to this count gives the played-so-far list directly, same pattern as purchaseCount
  const [isLottery, setIsLottery] = useState(false);

  // Kept fresh so the playback loop below always uses the latest
  // speed for its next scheduled step, without needing speed in that
  // effect's own deps — changing speed mid-playback this way just
  // changes the pace of what's still to come, rather than restarting
  // the current competition's playback from scratch.
  const speedRef = useRef(speed);
  // WHERE THE PLAYBACK IS IN THE COMPETITION'S OWN TIME (1.515.0).
  //
  // `anchorAt` is the recorded time of the last event played, `since` is
  // when it was played in real time - so the clock between two events is
  // anchorAt plus the real time since, multiplied by the speed. Null
  // `since` means paused, and the clock is simply anchorAt.
  //
  // A REF, NOT STATE, on purpose: it changes continuously and nothing
  // here should re-render for it. The component that shows it ticks
  // itself - see ReplayElapsed.
  const clockRef = useRef({ anchorAt: null, since: null });
  // The wait currently in flight: what is left of it in VIRTUAL
  // (competition) milliseconds, and when it was last scheduled.
  const pendingRef = useRef(null);
  const timerRef = useRef(null);
  useEffect(() => {
    const previous = speedRef.current;
    speedRef.current = speed;

    // RE-ANCHOR THE COMPETITION CLOCK FIRST. What has already been
    // watched was watched at `previous`; leaving the anchor where it
    // was would charge all of it at the new speed (see advanceClock).
    const runningClock = clockRef.current;
    if (previous !== speed && runningClock.since != null) {
      clockRef.current = {
        anchorAt: advanceClock({ ...runningClock, speed: previous, now: Date.now() }),
        since: Date.now(),
      };
    }

    // RESCHEDULE WHATEVER IS IN FLIGHT.
    //
    // Without this, changing the speed only affected the NEXT gap: a
    // replay opened at 1x sat on an hour-long wait no matter how many
    // times you pressed 600x. The pending wait is the one a person is
    // actually staring at, so it is the one that has to change.
    //
    // Work in VIRTUAL time — the competition's own milliseconds. What
    // has elapsed in real time since the wait was scheduled counts for
    // `previous` times as much of it, and what is left is rescheduled
    // at the new speed.
    const pending = pendingRef.current;
    if (!pending || previous === speed) return;
    // A PAUSED WAIT IS ALREADY FROZEN in virtual time, so a speed change
    // while paused changes nothing until it resumes.
    if (!playingRef.current || pending.since == null) return;
    const remaining = remainingWait({ ...pending, speed: previous, now: Date.now() });
    pendingRef.current = { ...pending, remaining, since: Date.now() };
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(pending.run, Math.max(1, remaining / speed));
  }, [speed]);

  // CHANGING THE OPTION RESTARTS IT. Ticking the box halfway through
  // cannot retrofit a lead-in that has already been skipped, and leaving
  // it to apply only to whatever is left would mean the box said one
  // thing and the playback did another.
  const asItHappenedRef = useRef(asItHappened);
  useEffect(() => {
    if (asItHappenedRef.current === asItHappened) return;
    asItHappenedRef.current = asItHappened;
    setPurchaseCount(0);
    setGuessCount(0);
    setPhase("playing");
    setPlaying(true);
    playingRef.current = true;
    clockRef.current = { anchorAt: null, since: null };
    setRunId((n) => n + 1);
  }, [asItHappened]);

  // Resets playback state whenever the competition changes.
  useEffect(() => {
    setPurchaseCount(0);
    setGuessCount(0);
    setPhase("playing");
    // The clock belongs to a competition, so it goes back with them
    // (1.515.0). Without this the demo page, which plays several in a
    // row, would open the second one showing the first one's elapsed
    // time until its first event landed.
    clockRef.current = { anchorAt: null, since: null };
  }, [comp?.id]);

  // Deliberately drives the whole sequence with a local (non-state)
  // `index` variable, rather than depending on an eventIndex state set
  // by the reset effect above — a real bug this replaced: on the very
  // same commit where comp?.id first changes, this effect (which also
  // depends on comp?.id) could still see eventIndex's stale value left
  // over from the *previous* competition's own, already-finished
  // playback, since a sibling effect's state update isn't visible
  // until a later render. If that stale value already exceeded the new
  // competition's own event count, playback skipped straight to
  // "revealed" without ever showing a single event — this is what made
  // competitions look like they had no selections uncovering at all,
  // and (combined with the identical gap-phase bug) like entire
  // competitions were being skipped.
  useEffect(() => {
    if (!comp) return;
    let index = 0;
    let cancelled = false;
    let timeoutId = null;
    // Initialized fresh for each competition's own playback run — in
    // the unlikely case lotteryLeadMs exceeds the competition's own
    // duration, it could already have started past its own conversion
    // point.
    setIsLottery(comp.lotteryConvertAt != null && comp.actualStartAt >= comp.lotteryConvertAt);

    // THE LEAD-IN: from the competition starting to its first event.
    //
    // Cut since this player was written, and it is half of why a replay
    // at 1x does not take as long as the competition did. Reported
    // 29 Sep 2026: "the replay at 1x doesn't play the competition as it
    // happened... it looks like the replay is playing the significant
    // part of it to shorten the time."
    //
    // Only with the box ticked, because on a competition whose first
    // purchase came hours in, this is hours of an empty picture - which
    // is exactly what happened, and exactly why it is not the default.
    // The speed buttons are how somebody gets through it.
    const leadIn = () =>
      leadInMs({
        asItHappened: asItHappenedRef.current,
        startedAt: comp.actualStartAt,
        firstEventAt: events[0]?.at,
      });

    function playNext() {
      if (cancelled) return;
      if (index >= events.length) {
        const reveal = () => {
          if (cancelled) return;
          setPhase("revealed");
          // NOTHING LEFT TO PLAY, so the transport says so rather than
          // offering a Pause that would do nothing.
          playingRef.current = false;
          setPlaying(false);
          pendingRef.current = null;
        };
        // Through the same pending record as every other wait, so the
        // hold before the reveal can be paused like anything else.
        pendingRef.current = { run: reveal, remaining: REVEALED_HOLD_MS, since: Date.now() };
        timeoutId = setTimeout(reveal, REVEALED_HOLD_MS / speedRef.current);
        timerRef.current = timeoutId;
        return;
      }
      const event = events[index];
      // THE CLOCK MOVES WITH THE PLAYBACK, not with a timer of its own.
      clockRef.current = { anchorAt: event.at, since: Date.now() };
      // Checked before this event is drawn, so the guess form swaps
      // for the lottery explanation at exactly the moment playback
      // reaches it — mirrors the real homepage's own isLottery check
      // (config.lotteryConvertAt against the current time) against
      // this event's own recorded time instead of the real clock.
      // Purchases still play through normally either side of this —
      // only guessing actually freezes, matching the real site.
      if (comp.lotteryConvertAt != null && event.at >= comp.lotteryConvertAt) {
        setIsLottery(true);
      }
      if (event.type === "purchase") {
        setPurchaseCount((c) => c + 1);
      } else if (event.type === "guess") {
        setGuessCount((c) => c + 1);
      }
      const nextEvent = events[index + 1];
      // THE TAIL: from the last event to the moment it was answered.
      //
      // The other half of the shortening. TAIL_GAP_MS is 800ms, which on
      // a competition whose last purchase was a day before the answer
      // stands in for a day. With the box ticked the real wait is
      // played, clamped at zero so a recording whose answeredAt precedes
      // its own last event cannot schedule a negative wait.
      const tail = tailMs({
        asItHappened: asItHappenedRef.current,
        answeredAt: comp.answeredAt,
        lastEventAt: event.at,
        fallback: TAIL_GAP_MS,
      });
      const gapMs = nextEvent ? nextEvent.at - event.at : tail;
      // THE REAL GAP, divided only by the chosen speed. No clamp: the
      // quiet stretches are part of what happened, and hiding them is
      // what would make this untrustworthy.
      //
      // Floored at 1ms only so a pair of events recorded in the same
      // millisecond still yields to the browser between frames.
      index += 1;
      // SCHEDULED SO A SPEED CHANGE CAN RESCHEDULE IT.
      //
      // The wait used to be a single setTimeout of gapMs/speed.
      // Changing the speed updated speedRef for the NEXT gap and left
      // the pending one running at the old pace — so opening a replay
      // at 1x and pressing 600x did nothing at all until that gap
      // elapsed, which on a four-hour competition is an hour of
      // nothing. Reported 18 Sep 2026: "I tried viewing at 600x and
      // didn't see any difference."
      //
      // The remaining VIRTUAL time is what is stored; the effect below
      // reschedules it whenever the speed changes.
      pendingRef.current = { run: playNext, remaining: gapMs, since: Date.now() };
      timeoutId = setTimeout(playNext, Math.max(1, gapMs / speedRef.current));
      timerRef.current = timeoutId;
    }
    const lead = leadIn();
    if (lead > 0) {
      // THE CLOCK STARTS AT THE START, not at the first event. Anchoring
      // here is what makes the elapsed readout count up from zero
      // through the lead-in; without it the clock would sit at null and
      // then jump to the first event's offset, which is the symptom that
      // gave this away.
      clockRef.current = { anchorAt: comp.actualStartAt, since: Date.now() };
      pendingRef.current = { run: playNext, remaining: lead, since: Date.now() };
      timeoutId = setTimeout(playNext, Math.max(1, lead / speedRef.current));
      timerRef.current = timeoutId;
    } else {
      playNext();
    }

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
      // THE RESCHEDULED TIMER TOO. A speed change replaces timeoutId
      // with one this closure never saw, so clearing only the original
      // would leave a closed replay still advancing in the background.
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
      pendingRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comp?.id, runId]);

  const pause = () => {
    if (!playingRef.current) return;
    const pending = pendingRef.current;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    if (pending && pending.since != null) {
      pendingRef.current = {
        ...pending,
        remaining: remainingWait({ ...pending, speed: speedRef.current, now: Date.now() }),
        // No anchor while paused: nothing is elapsing.
        since: null,
      };
    }
    // AND THE COMPETITION CLOCK WITH IT, frozen where it had reached
    // rather than where the last event was - otherwise pausing halfway
    // through a long quiet gap would jump the clock backwards.
    const clock = clockRef.current;
    if (clock.since != null) {
      clockRef.current = {
        anchorAt: advanceClock({ ...clock, speed: speedRef.current, now: Date.now() }),
        since: null,
      };
    }
    playingRef.current = false;
    setPlaying(false);
  };

  const resume = () => {
    if (playingRef.current) return;
    playingRef.current = true;
    setPlaying(true);
    // The clock starts again from wherever it froze.
    if (clockRef.current.anchorAt != null) clockRef.current = { ...clockRef.current, since: Date.now() };
    const pending = pendingRef.current;
    if (!pending) return;
    pendingRef.current = { ...pending, since: Date.now() };
    timerRef.current = setTimeout(pending.run, Math.max(1, pending.remaining / speedRef.current));
  };

  const restart = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    pendingRef.current = null;
    clockRef.current = { anchorAt: null, since: null };
    playingRef.current = true;
    setPlaying(true);
    setPurchaseCount(0);
    setGuessCount(0);
    setPhase("playing");
    setRunId((n) => n + 1);
  };

  return { phase, setPhase, purchaseCount, guessCount, isLottery, speedRef, playing, pause, resume, restart, clockRef };
}

// PLAY / PAUSE AND RESTART, the controls the partner's page had first.
export function ReplayTransportControls({ playing, phase, onPause, onResume, onRestart }) {
  const ended = phase === "revealed";
  return (
    <div style={{ display: "flex", gap: 6 }}>
      <button type="button" onClick={playing ? onPause : onResume} disabled={ended}>
        {playing ? "⏸ Pause" : "▶ Play"}
      </button>
      <button type="button" onClick={onRestart}>
        ↻ Restart
      </button>
    </div>
  );
}

// HOW FAR THROUGH IT IS. Counts rather than a clock: a replay's own time
// is the competition's, which can be days, and "4 of 37 purchases" is
// what somebody actually wants to know.
export function ReplayProgress({
  purchaseCount,
  totalPurchases,
  guessCount,
  totalGuesses,
  playing,
  phase,
  // THE COMPETITION'S OWN CLOCK (1.515.0), optional so a caller that has
  // no times to show renders exactly what it rendered before.
  clockRef,
  startedAt,
  endedAt,
  speedRef,
}) {
  const total = totalPurchases + totalGuesses;
  const played = purchaseCount + guessCount;
  const pct = total > 0 ? Math.round((played / total) * 100) : 0;
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ height: 4, background: "var(--border-color-subtle)", borderRadius: 2, overflow: "hidden" }}>
        <div
          style={{
            width: `${phase === "revealed" ? 100 : pct}%`,
            height: "100%",
            background: "var(--accent-blue)",
            transition: "width 200ms linear",
          }}
        />
      </div>
      <div style={{ fontSize: 12, opacity: 0.6, marginTop: 4 }}>
        {purchaseCount} of {totalPurchases} purchase{totalPurchases === 1 ? "" : "s"} · {guessCount} of {totalGuesses}{" "}
        entr{totalGuesses === 1 ? "y" : "ies"}
        {clockRef ? (
          <>
            {" · "}
            <ReplayElapsed
              clockRef={clockRef}
              startedAt={startedAt}
              endedAt={endedAt}
              speedRef={speedRef}
              playing={playing}
              phase={phase}
            />
          </>
        ) : null}
        {phase === "revealed" ? " · finished" : playing ? "" : " · paused"}
      </div>
    </div>
  );
}

// WHERE IN THE COMPETITION THIS IS (1.515.0), beside the counts.
//
// ITS OWN TICK, and that is the point of it being its own component. The
// player re-renders only when an event plays, which on a quiet stretch
// can be minutes of real time at 1x - so a clock living in the parent
// would sit still exactly where somebody most wants to see time passing.
// Ticking here re-renders one line of text.
//
// FOUR TIMES A SECOND rather than once: at 600x a second of real time is
// ten minutes of competition, and a once-a-second clock visibly jumps.
export function ReplayElapsed({ clockRef, startedAt, endedAt, speedRef, playing, phase }) {
  const [, tick] = useState(0);
  useEffect(() => {
    // Nothing to animate once it has finished, or while it is paused -
    // the clock is frozen and a timer would only spend battery.
    if (phase === "revealed" || !playing) return undefined;
    const id = setInterval(() => tick((n) => n + 1), 250);
    return () => clearInterval(id);
  }, [playing, phase]);
  const clock = clockRef?.current || {};
  const span = virtualElapsed({
    anchorAt: clock.anchorAt == null ? startedAt : clock.anchorAt,
    since: clock.anchorAt == null ? null : clock.since,
    speed: speedRef?.current || 1,
    startedAt,
    endedAt,
    now: Date.now(),
  });
  // A replay recorded before either time was stored has no span to show,
  // and inventing one would be worse than showing nothing.
  if (!span) return null;
  const elapsed = phase === "revealed" ? span.total : span.elapsed;
  return (
    <span data-replay-elapsed>
      {formatSpan(elapsed)} of {formatSpan(span.total)}
    </span>
  );
}

// THE BOX THAT PUTS THE QUIET BACK (1.537.0).
//
// Asked for on 29 Sep 2026: "I think the replay should have a checkbox
// to 'Replay as it happened'." Off by default, because on a competition
// that ran overnight this is a night of nothing - which is the honest
// recording, and the reason the speed buttons exist beside it.
export function ReplayAsItHappened({ value, onChange }) {
  return (
    <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, opacity: 0.85, cursor: "pointer" }}>
      <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} data-replay-as-it-happened />
      Replay as it happened
    </label>
  );
}

// The speed selector, identical in both players.
export function ReplaySpeedControls({ speed, setSpeed }) {
  return (
    <div style={{ display: "flex", gap: 6 }}>
      {SPEEDS.map((s) => (
        <button key={s} onClick={() => setSpeed(s)} style={{ fontWeight: speed === s ? 700 : 400, opacity: speed === s ? 1 : 0.6 }}>
          {s}x
        </button>
      ))}
    </div>
  );
}

// THE PUBLIC REPLAY'S ROUTES, in one place.
//
// Two callers open the same playback against the same endpoints — the
// Winners page in a modal, and /replay/<id> as a shareable page. Built
// separately they were three props duplicated verbatim, which is the
// same duplication that let the partner replay widget keep a broken
// copy of the frame logic while the buyer's route was fixed.
//
// Added 18 Sep 2026.
export function publicReplayProps(competitionId, demoRouteToken) {
  const base = ""; // same-origin — nginx proxies /api
  const q = demoRouteToken
    ? `&demoRouteToken=${encodeURIComponent(demoRouteToken)}`
    : "";
  const id = encodeURIComponent(competitionId);
  return {
    competitionId,
    // NO TOKEN: that is what makes it public. useAuthedImageUrl sends
    // no header when there is none to send.
    token: null,
    loadData: async () => {
      const res = await fetch(`${base}/api/replays/${id}?x=1${q}`);
      if (!res.ok) throw new Error("No replay available.");
      return res.json();
    },
    progressiveImageUrl: (regionCount) =>
      `${base}/api/replays/${id}/image?regionCount=${regionCount}${q}`,
    // THE SNAPSHOT'S OWN REVEAL, not the live competition's image — a
    // replay is meant to outlive the round it replays.
    revealedImageUrl: `${base}/api/replays/${id}/image?revealed=1${q}`,
    // NO SPEED OVERRIDE. Every viewer starts at 1x — the pace the
    // competition actually ran at.
    //
    // I set this to 600 on 18 Sep 2026 because a public viewer opened
    // on a still picture, and a test caught it: "A REPLAY STARTS AT THE
    // PACE IT WAS RECORDED AT ... Every player and the widget must
    // agree on this, or the same replay runs differently depending on
    // where it is watched." The widget is plain JavaScript and cannot
    // read this, so the site would have run at 600x and a partner's
    // embed at 1x — the divergence that test exists to prevent, and
    // one the project already fixed once when replays started at 2x.
    //
    // The real fault was that pressing 600x did nothing to the wait
    // already in flight. That is fixed; the buttons work now.
  };
}

// ---------------------------------------------------------------------
// THE COMPETITION AS IT WAS DISPLAYED, not just the image.
//
// Asked for on 20 Sep 2026: "I want to show the competition as it ran
// live so not just the competition fog/hidden image area. All the
// sections surrounding it."
//
// NOTHING HERE IS CAPTURED. A replay has never held a picture of the
// page — it holds the competition's own events, and two images. So the
// surrounding cards are REBUILT from that recording, using the same
// copy and layout the live page uses (App.jsx), rather than replayed
// from pixels. That is why it works on replays recorded months ago as
// well as on new ones, and why it costs nothing on disk.
//
// WHAT CANNOT BE REBUILT HONESTLY, and is therefore absent: the
// free-entry banner (its state was never recorded), the selection-assist
// card (it depended on the viewer's own saved spend preference), and the
// "My stats" box (different for every person watching).
// ---------------------------------------------------------------------

// THE REPLAY'S OWN CLOCK, in competition time.
//
// A countdown on a replay must count down at the speed the replay is
// running, not at the speed of the viewer's own day. Every played event
// is a fixed point — its recorded timestamp — and between events the
// clock advances in real time multiplied by the playback speed, so 600x
// burns through an empty night exactly as the picture does.
//
// It never runs past the next event the viewer has not reached yet,
// which keeps the countdown from announcing something the playback has
// not shown.
export function useReplayClock(comp, events, playedCount, speed) {
  const lastAt = playedCount > 0 ? events[playedCount - 1]?.at ?? null : comp?.actualStartAt ?? null;
  const nextAt = playedCount < events.length ? events[playedCount]?.at ?? null : comp?.answeredAt ?? null;
  const [now, setNow] = useState(lastAt ?? null);
  const nowRef = useRef(lastAt ?? null);
  const anchorRef = useRef({ at: lastAt ?? null, real: Date.now() });

  useEffect(() => {
    nowRef.current = lastAt ?? null;
    anchorRef.current = { at: lastAt ?? null, real: Date.now() };
    setNow(lastAt ?? null);
  }, [lastAt]);

  // A SPEED CHANGE RE-ANCHORS RATHER THAN REWINDING — the same rule the
  // playback loop follows for the wait it already has in flight.
  useEffect(() => {
    anchorRef.current = { at: nowRef.current, real: Date.now() };
  }, [speed]);

  useEffect(() => {
    if (lastAt == null) return undefined;
    const id = setInterval(() => {
      const { at, real } = anchorRef.current;
      if (at == null) return;
      const value = replayClockValue({ anchorAt: at, anchorReal: real, speed, nextAt, now: Date.now() });
      if (value == null) return;
      nowRef.current = value;
      setNow(value);
    }, 250);
    return () => clearInterval(id);
  }, [lastAt, nextAt, speed]);

  return now;
}

const REPLAY_CARD = {
  flex: "1 1 300px",
  border: "1px solid var(--border-color)",
  borderRadius: 8,
  padding: 12,
  boxSizing: "border-box",
};

// Mirrors App.jsx's own EndsInCountdown, against the replay's clock
// rather than the wall clock.
export function ReplayEndsIn({ finishesAt, now }) {
  if (!finishesAt || now == null) return null;
  const left = finishesAt - now;
  if (left <= 0) return null;
  return (
    <p style={{ margin: "0 0 8px", fontSize: 15, fontWeight: 600 }}>
      Competition ends in <strong>{formatLongCountdown(left)}</strong>
    </p>
  );
}

// The left-hand card on the live page: what a player was being asked to
// do, at the prices that applied at the time.
//
// Each sentence is dropped rather than guessed at when the recording
// does not carry its number — a replay saying "£0.00/pixel" would be
// inventing the terms of somebody's competition.
// THE RESULT, WHEN THE REPLAY REACHES IT (1.537.0).
//
// Reported 29 Sep 2026 against the live result panel: "the replay
// doesn't show the section in the screenshot so the winner isn't shown
// on the replay."
//
// The reveal block said what the answer was and stopped there. The
// PARTNER'S embedded replay widget has shown "Won by <name>" and the
// prize since 26 Sep - added because "a visitor watched a replay to the
// end, the picture was revealed, and nothing said what the answer had
// been or who won". Our own page got the answer half of that fix and
// not the winner half, so a partner's copy of our replay said more
// about our competition than we did.
//
// Lives here rather than in the modal so there is one definition of
// what a result looks like, which is why publicReplayProps is in this
// file too.
//
// EVERY LINE IS OPTIONAL, and that is not defensive coding: snapshots
// are written once and never rewritten, so a replay recorded before a
// field existed simply has not got it. Drawing "Prize: null" or an
// invented ticket number would be worse than drawing nothing.
export function ReplayResult({ comp, you, currencySymbol }) {
  if (!comp) return null;
  const symbol = comp.currencySymbol || currencySymbol || "";
  // "?" IS NOT A NAME. A masked recording of a competition nobody won
  // carries "?" where an unmasked one carries null - the replay widget
  // learned this first and the rule is the same here. A real masked
  // name is always "X***", never bare "?".
  const recorded = Object.prototype.hasOwnProperty.call(comp, "winnerUsername");
  const winner = comp.winnerUsername && comp.winnerUsername !== "?" ? comp.winnerUsername : null;
  return (
    <div style={{ marginBottom: 16 }} data-replay-result>
      <h4 style={{ marginTop: 0, marginBottom: 4 }}>{comp.wonByLottery ? "Lottery Drawn!" : "Solved!"}</h4>
      <p style={{ opacity: 0.8, margin: 0 }}>
        The answer was <strong>{comp.answer}</strong>
        {comp.wonByLottery ? ", resolved by a random draw among purchased pixels." : "."}
      </p>
      {recorded && (winner || comp.wonByLottery) ? (
        <p style={{ opacity: 0.8, margin: "6px 0 0" }} data-replay-winner>
          {winner ? (
            <>
              Won by <strong>{winner}</strong>
              {comp.wonByLottery ? " on the draw" : " with a correct guess"}
              {/* THE TICKET, in the site's own words. Only on a lottery,
                  and only where the recording carries it. */}
              {comp.wonByLottery && comp.winningTicket != null ? ` (ticket #${comp.winningTicket})` : ""}.
            </>
          ) : (
            "Nobody entered the draw, so there was no winner."
          )}
        </p>
      ) : null}
      {comp.prizeMoney != null && Number(comp.prizeMoney) > 0 ? (
        <p style={{ opacity: 0.8, margin: "6px 0 0" }} data-replay-prize>
          Prize:{" "}
          <strong>
            {symbol}
            {Number(comp.prizeMoney).toFixed(2)}
          </strong>
        </p>
      ) : null}
      {you?.won && (
        <p style={{ margin: "6px 0 0", color: "var(--accent-green)" }}>
          {comp.wonByLottery ? "The draw picked you." : "You won this one."}
        </p>
      )}
    </div>
  );
}

export function ReplayHowItWorksCard({ comp, currencySymbol, now }) {
  const symbol = comp?.currencySymbol || currencySymbol || "";
  const minutes = comp?.privateViewMs ? Math.round(comp.privateViewMs / 60000) : null;
  return (
    <div style={REPLAY_CARD}>
      <ReplayEndsIn finishesAt={comp?.finishesAt} now={now} />
      <p style={{ opacity: 0.8, margin: 0 }}>
        Drag on the image to select a rectangle.
        {comp?.pricePerPixel != null && (
          <>
            {" "}
            You'll be quoted a price ({symbol}
            {Number(comp.pricePerPixel).toFixed(2)}/pixel), and after payment your selection is cut out of the real
            hidden image and shown to you privately
            {minutes ? ` for ${minutes} minute${minutes === 1 ? "" : "s"}` : ""} before it becomes public for everyone.
          </>
        )}
        {comp?.prizeMoney > 0 && (
          <>
            {" "}
            Correctly guess what the hidden image is for a chance to win{" "}
            <strong>
              {symbol}
              {Number(comp.prizeMoney).toFixed(2)}
            </strong>
            !
          </>
        )}{" "}
        <span style={{ opacity: 0.6 }}>
          ({comp?.answerMode === "exact" ? "Exact match required" : "Fuzzy match accepted"})
        </span>
      </p>
    </div>
  );
}

// The Competition Status card, rebuilt from the recording. Every row
// that depends on a number an older snapshot does not carry is left out
// rather than shown empty.
export function ReplayStatusCard({ comp, isLottery, now }) {
  const minutes = comp?.privateViewMs ? Math.round(comp.privateViewMs / 60000) : null;
  return (
    <div style={REPLAY_CARD}>
      <div style={{ fontSize: 13, opacity: 0.7, marginBottom: 8 }}>Competition status</div>
      <div style={{ fontSize: 13, display: "grid", gridTemplateColumns: "auto 1fr", rowGap: 6, columnGap: 10 }}>
        <span style={{ opacity: 0.6 }}>Status:</span>
        <span style={isLottery ? { color: "var(--accent-yellow)" } : undefined}>
          {isLottery ? "Lottery draw pending" : "In Play"}
        </span>
        <span style={{ opacity: 0.6 }}>Type:</span>
        <span>{isLottery ? "Lottery" : "Competition"}</span>
        {minutes != null && (
          <>
            <span style={{ opacity: 0.6 }}>Private view length:</span>
            <span>
              {minutes} minute{minutes === 1 ? "" : "s"}
            </span>
          </>
        )}
        {!isLottery && comp?.lotteryConvertAt && now != null && (
          <>
            <span style={{ opacity: 0.6 }}>Converts to lottery in:</span>
            <span>{formatLongCountdown(comp.lotteryConvertAt - now)}</span>
          </>
        )}
        {isLottery && comp?.finishesAt && now != null && (
          <>
            <span style={{ opacity: 0.6 }}>Draw happens in:</span>
            <span style={{ color: "var(--accent-yellow)" }}>{formatLongCountdown(comp.finishesAt - now)}</span>
          </>
        )}
        <span style={{ opacity: 0.6 }}>Started:</span>
        <span>{comp?.actualStartAt ? siteDateTime(comp.actualStartAt) : "—"}</span>
        <span style={{ opacity: 0.6 }}>Finishes:</span>
        <span>{comp?.finishesAt ? siteDateTime(comp.finishesAt) : "—"}</span>
      </div>
    </div>
  );
}

// FULL COMPETITION OR JUST THE IMAGE.
//
// "Depending on how the partner website is displaying their competitions
// the fog/hidden image competition area might be difficult to see as it
// might appear relatively small. So I'd like to toggle between the
// competition as it was displayed and just the fog/hidden image area."
export function ReplayViewToggle({ view, setView }) {
  return (
    <div style={{ display: "flex", gap: 6 }}>
      {[
        ["full", "Full competition"],
        ["image", "Image only"],
      ].map(([key, label]) => (
        <button
          key={key}
          onClick={() => setView(key)}
          aria-pressed={view === key}
          style={{ fontWeight: view === key ? 700 : 400, opacity: view === key ? 1 : 0.6 }}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------
// THE REPLAY FROM THE VIEWER'S OWN POINT OF VIEW (1.398.0, 20 Sep 2026).
//
// "each logged in user would see the replay from their point of view.
// The public replays wouldn't be able to do this."
//
// WHICH IS EXACTLY HOW IT WORKS. The server marks which purchases and
// guesses belong to the person asking — by user id, held in the snapshot
// and never sent — and hands back indexes into the arrays it was already
// sending. A public viewer is nobody, so `you` is absent and none of
// this renders.
// ---------------------------------------------------------------------

// The boxes drawn over the image: the area being bought as it is bought,
// and this viewer's own pixels.
//
// THE SELECTION BOX IS THE ONE A PLAYER SAW THEMSELVES. "In a replay if
// the user used it to select an area then they might expect to see a box
// with an arrow which appears when selecting an area so this should be
// there on the replay." The rectangle is recorded exactly; the drag that
// produced it never was, so what is drawn is the area that was bought,
// at the moment it was bought, with what it cost.
export function ReplayImageOverlay({ comp, purchaseCount, mineIndexes }) {
  const width = comp?.imageWidth;
  const height = comp?.imageHeight;
  if (!width || !height) return null;
  const pct = (r) => ({
    position: "absolute",
    left: `${(r.x / width) * 100}%`,
    top: `${(r.y / height) * 100}%`,
    width: `${(r.w / width) * 100}%`,
    height: `${(r.h / height) * 100}%`,
    boxSizing: "border-box",
    pointerEvents: "none",
  });
  const mine = new Set(mineIndexes || []);
  const lastIndex = purchaseCount - 1;
  const last = lastIndex >= 0 ? comp.regions?.[lastIndex] : null;
  const price =
    last && comp.pricePerPixel != null ? (last.w * last.h * comp.pricePerPixel).toFixed(2) : null;
  return (
    <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
      {(comp.regions || []).slice(0, Math.max(0, purchaseCount)).map((r, i) =>
        mine.has(i) ? (
          <div key={`mine-${i}`} style={{ ...pct(r), border: "2px solid var(--accent-blue)" }} />
        ) : null
      )}
      {last && (
        <div style={{ ...pct(last), border: "2px solid var(--accent-yellow)", background: "rgba(212,160,23,.18)" }}>
          {price && (
            <div
              style={{
                position: "absolute",
                top: "-1.4em",
                left: 0,
                fontSize: 11,
                whiteSpace: "nowrap",
                color: "var(--accent-yellow)",
                textShadow: "0 1px 2px rgba(0,0,0,.8)",
              }}
            >
              ▼ {comp.currencySymbol || ""}
              {price}
              {last.isFreeEntry ? " · free entry" : ""}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// MY STATS, AS AT THIS POINT IN THE PLAYBACK — not as of today.
//
// The live site's own box shows an account-wide credit balance and a
// lifetime count of competitions entered. Both would be anachronistic
// here: a replay is a record of one competition on one day, and a
// balance from this afternoon has nothing to do with it. What belongs
// here is what this person had done in THIS competition by this point,
// which counts up as the replay reaches it.
export function ReplayMyStatsPanel({ pixelsBought, entriesSubmitted, usedFreeEntry }) {
  return (
    <div style={{ width: "100%", border: "1px solid var(--accent-blue)", borderRadius: 8, padding: 12, boxSizing: "border-box" }}>
      <div style={{ fontSize: 13, opacity: 0.6, marginBottom: 4 }}>My stats</div>
      <div style={{ fontSize: 14, marginBottom: 2 }}>My purchases: {pixelsBought}</div>
      <div style={{ fontSize: 14, marginBottom: 2 }}>My entries: {entriesSubmitted}</div>
      {usedFreeEntry && <div style={{ fontSize: 13, opacity: 0.8 }}>Includes your free entry</div>}
      {/* WHETHER THEY WON IS NOT HERE. It belongs to the reveal, at the
          end — a replay that tells you the outcome while it is still
          playing has given the game away. */}
      <div style={{ fontSize: 12, opacity: 0.55, marginTop: 6 }}>Your own purchases are outlined on the image.</div>
    </div>
  );
}

// THE FREE-ENTRY BANNER, only where the competition itself recorded that
// free entry was on when it started (frozen by maybeLockInStart).
//
// A replay of a competition that ran before 1.398.0 carries no such
// record, and no banner is shown — rebuilding one from today's settings
// would describe today's site, not that competition.
export function ReplayFreeEntryBanner({ comp, you }) {
  if (!comp?.freeEntryEnabledAtStart) return null;
  const routes = [comp.freeEntryPixelAtStart ? "a free pixel" : null, comp.freeEntryGuessAtStart ? "a free guess" : null].filter(
    Boolean
  );
  if (!routes.length) return null;
  return (
    <div
      style={{
        border: "1px solid var(--accent-green)",
        borderRadius: 8,
        padding: 12,
        marginBottom: 16,
        color: "var(--accent-green)",
        fontSize: 13,
      }}
    >
      Free entry was available on this competition — {routes.join(" or ")}, one per person.
      {you?.usedFreeEntry ? " You took yours." : ""}
    </div>
  );
}
